import {
    Box,
    lighten,
    Menu,
    menuClasses,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { visuallyHidden } from '@mui/utils'
import { Features } from 'c1gen/features'
import { Permissions } from 'c1gen/permissions'
import { Roles } from 'c1gen/roles'
import { C1Button, C1IconButton, palette } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { skeletonFadeInSx } from 'c1ui/theme/C1Transitions'
import { EmptyList } from 'components/core/empty-list/index'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CustomColumnConfigurationUI } from 'components/core/list/C1Table/CustomColumnConfigurationUI'
import { useGetTableCheckboxColumn } from 'components/core/list/C1Table/useGetTableCheckboxColumn'
import { TableHeaderStyles } from 'components/core/list/styles'
import { C1TablePagination } from 'components/core/pagination/C1TablePagination'
import { useFeatureCheckerLazy, usePermissionCheckerLazy, useRoleCheckerLazy } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { ConnectorAccuracyStatus } from 'components/pages/accessreview/details/campaign_accuracy/getConnectorAccuracyStatus'
import { useLocalStorage } from 'hooks/local-storage'
import useGetDefaultPageSize from 'hooks/useGetDefaultPageSize'
import { TableErrorFallback } from 'lib/error-fallback'
import { ChevronDown, ChevronUp, CircleAlert, Columns3, RefreshCw } from 'lucide-react'
import { usePathname } from 'next/navigation'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { FormattedMessage, useIntl } from 'react-intl'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'
import { C1TableReduxStyles } from './C1TableReduxStyles'
import { useTableFocusManagement } from './useTableFocusManagement'
import { useTableKeyboardNavigation } from './useTableKeyboardNavigation'

export type SortDirectionType = 'asc' | 'desc'

export type C1TableCellProps<T, ExtraProps> = {
    row: T;
    extras: ExtraProps;
    toggleRowIsExpanded: () => void;
    rowIsExpanded: boolean;
    rowIsSelected: boolean;
}

export type C1TableGroupHeaderRow<T> = {
    id: string
    groupKey: string
    groupLabel: React.ReactNode | null
    rows: T[]
}

export type C1TableReduxColumn<T, ExtraProps = object> = {
    field: string
    label?: React.ReactNode | FC<{ datas: ReadonlyArray<T>; extras: ExtraProps }>
    alignmentOverrides?: 'margin1' | 'margin2'
    align?: 'left' | 'right' // default left
    Component: FC<C1TableCellProps<T, ExtraProps>>
    shouldRender?: (hasFeature: (feature: Features) => boolean | null,
        hasPermissions: (permission: Permissions) => boolean | null,
        hasRoles: (role: Roles) => boolean | null,
        extras: ExtraProps) => boolean
    onSort?: (extras: ExtraProps, direction: SortDirectionType) => void
    isSticky?: (extras: ExtraProps) => boolean
    minWidth?: string | ((extras: ExtraProps) => string)
    maxWidth?: string | ((extras: ExtraProps) => string)
    width?: string | ((extras: ExtraProps) => string)
    flex?: number
    isError?: (row: T) => boolean
    loadingElement?: React.ReactNode
    status?: (row: T) => number
    isLocked?: boolean
}

type GetId<T> = (row: T, i?: number) => string
export type C1TableVariant = 'default' | 'minimal'

export type C1TableReduxProps<T, ExtraProps> = {
    canLoadMore?: boolean
    cols: C1TableReduxColumn<T, ExtraProps>[]
    data: ReadonlyArray<ReadonlyArray<T>>
    groupedData?: ReadonlyArray<ReadonlyArray<C1TableGroupHeaderRow<T>>>
    defaultSortByColumn?: string
    emptyState?: React.ReactNode
    error?: SerializedErrorType
    getId?: GetId<T>
    isRowError?: (row: T) => boolean
    isError: boolean
    isLoading: boolean
    isBackgroundLoading?: boolean
    onPageChange?: (pageNumber: number) => Promise<void>
    page: number
    pageSize?: number
    totalRowCount?: number
    staticExtraProps?: ExtraProps
    shouldReload?: boolean
    onReload?: () => void
    tableMetaData?: React.ReactElement
    defaultSortDirection?: SortDirectionType
    extraFooterActions?: React.ReactElement
    selections?: T[]
    disabledSelections?: T[]
    setSelections?: (selections: T[]) => void
    onHeaderCheckboxClick?: (event: React.ChangeEvent<HTMLInputElement>) => void
    isSelectingViaFilter?: boolean
    ExpandedContent?: FC<C1TableCellProps<T, ExtraProps>>
    onRowClick?: (event: React.MouseEvent | React.KeyboardEvent, row: T, extraProps: ExtraProps) => void
    // When the total count is actually known, we can show it in the pagination
    totalCount?: number
    columnConfigurationIsActive?: boolean
    // Custom key for column configuration storage. If not provided, uses pathname.
    columnConfigurationKey?: string
    availableColumns?: C1TableReduxColumn<T, ExtraProps>[]
    // 'default' shows full table with pagination and borders, 'minimal' is compact without pagination/borders
    variant?: C1TableVariant
    // Function to generate a custom aria-label for each row's focus anchor (for screen readers)
    getRowAriaLabel?: (row: T) => string
}

type C1TableBody<T, ExtraProps> = {
    rows?: ReadonlyArray<T>
    groupedRows?: ReadonlyArray<C1TableGroupHeaderRow<T>>
    getId: GetId<T>
    staticExtraProps: ExtraProps
    isRowError: (row: T) => boolean
    emptyState: React.ReactNode
    selectionsAsMap?: Map<string, T>
    focusAnchorRefs?: React.MutableRefObject<Map<number, HTMLButtonElement>>
    initialFocusedRowIndex?: number
} & Pick<C1TableReduxProps<T, ExtraProps>, 'cols' | 'isError' | 'isLoading' | 'pageSize' | 'ExpandedContent' | 'onRowClick' | 'getRowAriaLabel'>

const defaultFocusAnchorRefs = { current: new Map<number, HTMLButtonElement>() }

export const C1TableBody = <T, ExtraProps>({
    cols,
    rows,
    groupedRows,
    isRowError,
    emptyState,
    isError,
    isLoading,
    getId,
    staticExtraProps,
    ExpandedContent,
    onRowClick,
    selectionsAsMap,
    focusAnchorRefs = defaultFocusAnchorRefs,
    initialFocusedRowIndex = 0,
    getRowAriaLabel,
}: C1TableBody<T, ExtraProps>): React.ReactElement => {
    const hasFeature = useFeatureCheckerLazy()
    const hasPermissions = usePermissionCheckerLazy()
    const hasRoles = useRoleCheckerLazy()
    const filteredCols = cols.filter(
        (c) => (c.shouldRender ? c.shouldRender(hasFeature, hasPermissions, hasRoles, staticExtraProps) : true),
    )
    const numOfCols = filteredCols.length

    const [expandedRowsMap, setExpandedRowsMap] = useState<Set<string>>(new Set())

    const toggleRowIsExpanded = (id: string): void => {
        const newSet = new Set(expandedRowsMap)
        if (newSet.has(id)) {
            newSet.delete(id)
        } else {
            newSet.add(id)
        }
        setExpandedRowsMap(newSet)
    }

    const renderRow = (row: T, rowIndex: number): React.ReactElement => {
        const rowId = getId(row, rowIndex)
        const isExpanded = expandedRowsMap.has(rowId)
        const isSelected = selectionsAsMap?.has(rowId) ?? false

        return <C1TableRow
            key={rowId}
            cols={filteredCols}
            row={row}
            staticExtraProps={staticExtraProps}
            isRowError={isRowError}
            onClick={onRowClick}
            rowId={rowId}
            rowIndex={rowIndex}
            rowIsExpanded={isExpanded}
            toggleRowIsExpanded={(): void => toggleRowIsExpanded(rowId)}
            rowIsSelected={isSelected}
            ExpandedContent={ExpandedContent}
            initialTabIndex={initialFocusedRowIndex === rowIndex ? 0 : -1}
            focusAnchorRef={(el): void => {
                if (el) {
                    focusAnchorRefs.current.set(rowIndex, el)
                } else {
                    focusAnchorRefs.current.delete(rowIndex)
                }
            }}
            getRowAriaLabel={getRowAriaLabel}
        />
    }

    if (isError) {
        return <TableRow key="error">
            <Box sx={{ gridColumn: `1 / span ${numOfCols}` }}>
                <EmptyList
                    icon={<C1Icon icon={CircleAlert} />}
                    title={<FormattedMessage id="component_c1_table_error" defaultMessage="Something went wrong" />}
                    isError
                />
            </Box>
        </TableRow>
    }

    if (isLoading) {
        return <>
            {[...Array(5)].map((_, i) => (
                <TableRow key={`skeleton-row-${i}`} sx={skeletonFadeInSx}>
                    {filteredCols.map((column) => {
                        return (
                            <TableCell key={`skeleton-${column.field}`} sx={C1TableReduxStyles.skeletonRow}>
                                {column.loadingElement || <Skeleton sx={C1TableReduxStyles.skeleton} />}
                            </TableCell>
                        )
                    })}
                </TableRow>
            ))}
        </>
    }

    // Show empty state if no rows
    if ((!rows || rows.length === 0) && (!groupedRows || groupedRows.length === 0)) {
        return <TableRow>
            <TableCell key="empty-state" sx={{ gridColumn: `1 / span ${numOfCols}`, backgroundColor: 'background.default' }}>
                {emptyState}
            </TableCell>
        </TableRow>
    }

    // Render grouped rows if provided
    if (groupedRows?.length) {
        let globalRowIndex = 0
        return <>
            {groupedRows.map((group) => {
                const groupStartIndex = globalRowIndex
                globalRowIndex += group.rows.length
                return (
                    <React.Fragment key={group.id}>
                        {group.groupLabel && <Box
                            component="div"
                            sx={sxc(C1TableReduxStyles.groupHeader, {
                                gridColumn: '1 / -1',
                                borderBottom: '1px solid',
                                borderBottomColor: 'border.subtle',
                                backgroundColor: 'background.accent',
                            })}
                        >
                            {group.groupLabel}
                        </Box>}
                        {group.rows.map((row, localIndex) => renderRow(row, groupStartIndex + localIndex))}
                    </React.Fragment>
                )
            })}
        </>
    }

    // Render regular rows
    return <>{rows?.map(renderRow)}</>
}

const C1TableRow = <T, ExtraProps>({
    cols,
    row,
    staticExtraProps,
    isRowError,
    rowId,
    rowIndex,
    ExpandedContent,
    rowIsExpanded,
    rowIsSelected,
    toggleRowIsExpanded,
    onClick,
    initialTabIndex,
    focusAnchorRef,
    getRowAriaLabel,
}: {
    cols: C1TableReduxColumn<T, ExtraProps>[]
    row: T
    isRowError: (row: T) => boolean
    staticExtraProps: ExtraProps
    rowId: string
    rowIndex: number
    ExpandedContent?: FC<C1TableCellProps<T, ExtraProps>>
    rowIsExpanded: boolean
    rowIsSelected: boolean
    toggleRowIsExpanded: () => void
    onClick?: (event: React.MouseEvent | React.KeyboardEvent, row: T, extraProps: ExtraProps) => void
    initialTabIndex: number
    focusAnchorRef: (el: HTMLButtonElement | null) => void
    getRowAriaLabel?: (row: T) => string
}): React.ReactElement => {
    const intl = useIntl()
    const rowError = isRowError(row)

    const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>): void => {
        const targetElement = e.target as HTMLElement
        // if the click is not coming from a button, a, input, select, or a menu, then trigger the onClick
        // also ensure its coming from a table cell and not a sticky column
        const tableCell = targetElement.closest(`.${tableCellClasses.root}`)
        if (!targetElement.closest(`button, a, input, select, .${menuClasses.root} `) && tableCell && !tableCell.getAttribute('data-sticky')) {
            onClick && onClick(e, row, staticExtraProps)
        }
    }

    const handleFocusAnchorKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
        if ((e.key === 'Enter' || (e.key === ' ' && !e.shiftKey)) && onClick) {
            e.preventDefault()
            onClick(e, row, staticExtraProps)
        }
    }

    return (
        <>
            <TableRow
                key={rowId}
                data-dd-action-name={onClick ? 'Select row' : undefined}
                data-row-index={rowIndex}
                sx={{
                    '&:focus-within:has(.focus-anchor:focus)': {
                        [`& .${tableCellClasses.root}`]: {
                            position: 'relative',
                            boxShadow: `inset 0 -2px 0 0 ${palette.primary.main}, inset 0 2px 0 0 ${palette.primary.main}`,
                        },
                        [`& .${tableCellClasses.root}:first-of-type:before, & .${tableCellClasses.root}:last-of-type:before`]: {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            width: '2px',
                            bottom: 0,
                            backgroundColor: palette.primary.main,
                        },
                        [`& .${tableCellClasses.root}:last-of-type:before`]: {
                            right: 0,
                        },
                        [`& .${tableCellClasses.root}:first-of-type:before`]: {
                            left: 0,
                        },
                    },
                    '&:hover': {
                        [`& .${tableCellClasses.root}`]: onClick ? {
                            boxShadow: `0px -1px 0px ${palette.primary.extraLight}`,
                            borderBottomColor: 'primary.extraLight',
                            backgroundColor: lighten(palette.primary.extraLight, 0.5),
                            cursor: 'pointer',
                        } : {
                            boxShadow: `0px -1px 0px ${palette.grey[150]}`,
                            borderBottomColor: 'grey.150',
                            backgroundColor: 'grey.50',
                            cursor: 'default',
                        },
                    },
                }}
                onClick={onClick ? (e): void => handleRowClick(e) : undefined}
            >
                {cols.map((column, columnIndex) => {
                    const isSticky = column.isSticky && column.isSticky(staticExtraProps)
                    const isStickyAndAlignRight = isSticky && column.align !== 'left'
                    return (
                        <TableCell
                            key={`${rowId}-${column.field}`}
                            data-sticky={isSticky || undefined}
                            sx={sxc(
                                [
                                    isSticky,
                                    C1TableReduxStyles.sticky,
                                ],
                                C1TableReduxStyles.cell,
                                { justifyContent: isStickyAndAlignRight || column.align === 'right' ? 'flex-end' : 'flex-start' },
                                [columnIndex === 0 && rowError, C1TableReduxStyles.errorCell],
                                [column.status?.(row) === ConnectorAccuracyStatus.ERROR, C1TableReduxStyles.errorCell],
                                [column.status?.(row) === ConnectorAccuracyStatus.WARNING, C1TableReduxStyles.warningCell],
                            )}
                        >
                            {/* Visually hidden focus anchor for keyboard navigation */}
                            {columnIndex === 0 && (
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        component="button"
                                        className="focus-anchor"
                                        ref={focusAnchorRef}
                                        tabIndex={initialTabIndex}
                                        onKeyDown={handleFocusAnchorKeyDown}
                                        aria-label={getRowAriaLabel?.(row) ?? intl.formatMessage(
                                            { id: 'table_row_focus_anchor', defaultMessage: 'Row {index}' },
                                            { index: rowIndex + 1 },
                                        )}
                                        aria-expanded={ExpandedContent ? rowIsExpanded : undefined}
                                        aria-selected={rowIsSelected}
                                        sx={visuallyHidden}
                                    />
                                </Box>
                            )}
                            <column.Component
                                row={row}
                                extras={staticExtraProps}
                                rowIsExpanded={rowIsExpanded}
                                toggleRowIsExpanded={toggleRowIsExpanded}
                                rowIsSelected={rowIsSelected}
                            />
                        </TableCell>
                    )
                })}
            </TableRow>
            {(rowIsExpanded && ExpandedContent) && (<TableRow>
                <TableCell
                    key={`${rowId}-expanded`}
                    sx={{
                        ...C1TableReduxStyles.cell,
                        gridColumn: `1 / span ${cols.length}`,
                        padding: 0,
                        '&:first-of-type': { paddingLeft: 0 },
                    }}
                >
                    <ExpandedContent
                        row={row}
                        extras={staticExtraProps}
                        rowIsExpanded={rowIsExpanded}
                        toggleRowIsExpanded={toggleRowIsExpanded}
                        rowIsSelected={rowIsSelected}
                    />
                </TableCell>
            </TableRow>)}

        </>
    )
}

const generateHash = async (str: string): Promise<string> => {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    return hashHex.substring(0, 8)
}

export const C1TableRedux = <T, ExtraProps = object>({
    canLoadMore,
    cols,
    data,
    groupedData,
    defaultSortByColumn = '',
    emptyState = <EmptyList />,
    error,
    getId = (x, i): string => `${(x as { id: string }).id}-${i}`,
    isRowError = (): boolean => false,
    isError,
    isLoading,
    isBackgroundLoading = false,
    onPageChange,
    page,
    pageSize: _pageSize,
    shouldReload,
    onReload,
    totalRowCount: _totalRowCount,
    tableMetaData,
    staticExtraProps: _staticExtraProps = {} as ExtraProps,
    defaultSortDirection = 'desc',
    ExpandedContent,
    selections,
    disabledSelections,
    setSelections,
    onHeaderCheckboxClick,
    isSelectingViaFilter,
    extraFooterActions,
    onRowClick,
    totalCount: _totalCount,
    columnConfigurationIsActive,
    columnConfigurationKey,
    availableColumns: _availableColumns,
    variant = 'default',
    getRowAriaLabel,
}: C1TableReduxProps<T, ExtraProps>): React.ReactElement => {
    const intl = useIntl()
    const staticExtraProps = useRef(_staticExtraProps).current
    const tableScrollingContainerRef = useRef<HTMLDivElement>(null)
    const tableBodyRef = useRef<HTMLTableSectionElement>(null)
    const hasFeature = useFeatureCheckerLazy()
    const hasPermissions = usePermissionCheckerLazy()
    const hasRoles = useRoleCheckerLazy()
    const [sortByColumn, setSortByColumn] = useState<string>(defaultSortByColumn)
    const [sortDirection, setSortDirection] = useState<SortDirectionType>(defaultSortDirection)
    const [columnCustomizerAnchorEl, setColumnCustomizerAnchorEl] = useState<null | HTMLElement>(null)

    const defaultPageSize = useGetDefaultPageSize()
    const pageSize = _pageSize ?? defaultPageSize

    const pathname = usePathname()
    const [tableConfigHash, setTableConfigHash] = useState<string>('')

    // Use availableColumns if provided, otherwise fall back to cols
    const availableColumns = _availableColumns || cols

    useEffect(() => {
        if (!columnConfigurationIsActive) {
            setTableConfigHash('')
            return
        }

        const columnFields = availableColumns.map((col) => col.field).join('|')
        const keyBase = columnConfigurationKey || pathname
        generateHash(keyBase + columnFields).then(setTableConfigHash)
    }, [columnConfigurationIsActive, columnConfigurationKey, pathname, availableColumns])

    const defaultColumnKeys = useMemo(() => cols.filter((col) => !col.isLocked).map((col) => col.field), [cols])

    // Get total row count for current page
    const currentPageRows = data ? data[page] : []
    const totalRowsOnPage = currentPageRows?.length ?? 0

    // This fixes going back to 0th page when we are on X page and do a filter or invalidate the list
    page = page + 1 > data?.length ? 0 : page

    const {
        focusedRowIndexRef,
        focusAnchorRefs,
        updateFocusedRow,
        handleTableFocus,
    } = useTableFocusManagement({ totalRowsOnPage, page })

    const { handleTableKeyDown } = useTableKeyboardNavigation({
        totalRowsOnPage,
        focusedRowIndexRef,
        updateFocusedRow,
        rowsOnPage: currentPageRows,
        selections,
        setSelections,
        disabledSelections,
        getId,
    })

    // When the page changes, scroll to the top of the table
    useEffect(() => {
        if (tableScrollingContainerRef.current) tableScrollingContainerRef.current.scrollTop = 0
    }, [page])

    // Use a single localStorage key for all table customizations
    const [allColConfigs, setAllColConfigs] = useLocalStorage<Record<string, string[]>>(
        'c1-table-column-customizations',
        {},
    )

    const handleColumnConfigChange = (newSelectedKeys: string[]): void => {
        setAllColConfigs({ ...allColConfigs, [tableConfigHash]: newSelectedKeys })
    }

    const handleResetColumnConfig = (): void => {
        const newCustomizations = { ...allColConfigs }
        delete newCustomizations[tableConfigHash]

        setAllColConfigs(newCustomizations)
        setColumnCustomizerAnchorEl(null)
    }

    const handleSort = useCallback(
        (c: C1TableReduxColumn<T, ExtraProps>): void => {
            let direction: SortDirectionType = 'desc'
            if (sortByColumn === c.field && sortDirection === 'desc') {
                direction = 'asc'
            }
            setSortByColumn(c.field)
            setSortDirection(direction)
            c.onSort?.(staticExtraProps, direction)
        },
        [staticExtraProps, sortByColumn, sortDirection],
    )

    // Helper function to check if a column should be rendered based on permissions
    const shouldRenderColumn = useCallback(
        (col: C1TableReduxColumn<T, ExtraProps>): boolean => (
            col.shouldRender ? col.shouldRender(hasFeature, hasPermissions, hasRoles, staticExtraProps) : true
        ),
        [hasFeature, hasPermissions, hasRoles, staticExtraProps],
    )

    const { column: checkboxColumn, selectionsAsMap } = useGetTableCheckboxColumn<T, ExtraProps>(
        selections,
        disabledSelections,
        setSelections,
        getId,
        data[page],
        onHeaderCheckboxClick,
        isSelectingViaFilter,
    )

    const canBeConfiguredCols = useMemo(() => availableColumns.filter(shouldRenderColumn), [availableColumns, shouldRenderColumn])

    const thisTableSavedColConfig = allColConfigs?.[tableConfigHash] || null

    const filteredCols = useMemo(() => {
        const colsToDisplay: C1TableReduxColumn<T, ExtraProps>[] = []

        const isCustomizationActive = columnConfigurationIsActive

        if (!isCustomizationActive || !thisTableSavedColConfig) {
            // If we aren't customizing, just filter based on shouldRender
            colsToDisplay.push(...(cols.filter(shouldRenderColumn)))
        } else {
            // Otherwise, we need to order the columns based on the saved configuration
            const lockedCols = canBeConfiguredCols.filter((c) => c.isLocked)
            const customizableCols = canBeConfiguredCols.filter((c) => !c.isLocked)

            const customizableColsMap = new Map(customizableCols.map((c) => [c.field, c]))

            const orderedCustomizableCols = thisTableSavedColConfig
                .map((fieldKey) => customizableColsMap.get(fieldKey))
                .filter((col): col is C1TableReduxColumn<T, ExtraProps> => col !== undefined)

            colsToDisplay.push(...orderedCustomizableCols, ...lockedCols)
        }

        if (checkboxColumn) colsToDisplay.unshift(checkboxColumn)

        return colsToDisplay
    }, [columnConfigurationIsActive,
        thisTableSavedColConfig, cols, shouldRenderColumn, canBeConfiguredCols,
        checkboxColumn])

    let grid = ''
    filteredCols.forEach((col) => {
        if (col.isSticky && col.isSticky(staticExtraProps)) {
            grid += ' max-content'
            return
        }

        const getValue = (val): string => ((val && typeof val === 'function') ? val(staticExtraProps) : val)

        if (col.maxWidth) {
            grid += ` ${getValue(col.maxWidth)}`
        } else if (col.minWidth) {
            grid += ` minmax(${getValue(col.minWidth)}, max-content)`
        } else if (col.width) {
            grid += ` minmax(${getValue(col.width)}, ${getValue(col.width)})`
        } else {
            grid += ` minmax(min-content, ${col.flex === undefined ? 1 : col.flex}fr)`
        }
    })

    const configureColumnsButton = useMemo(() => <Tooltip
        placement="bottom"
        title={intl.formatMessage({ id: 'component_table_column_customization_title', defaultMessage: 'Configure columns' })}
    >
        <C1IconButton
            id="column-customizer-button"
            aria-controls={columnCustomizerAnchorEl ? 'column-customizer-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={columnCustomizerAnchorEl ? 'true' : undefined}
            onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                setColumnCustomizerAnchorEl(event.currentTarget)
            }}
            aria-label={intl.formatMessage({ id: 'component_table_column_customization_title', defaultMessage: 'Configure columns' })}
            variant="text"
        >
            <C1Icon icon={Columns3} size="sm" />
        </C1IconButton>
    </Tooltip>, [columnCustomizerAnchorEl, intl])

    return <ErrorBoundary FallbackComponent={TableErrorFallback}>
        <TableContainer
            sx={sxc(
                C1TableReduxStyles.tableContainer,
                [!onPageChange || data?.[0]?.length === 0, { borderRadius: C1BorderRadius.large }],
                [isError, { borderColor: 'error.light' }],
                [variant === 'minimal', {
                    marginTop: 0,
                    borderRadius: 0,
                    boxShadow: 'none',
                    border: 'none',
                    borderTopWidth: 1,
                    borderTopStyle: 'solid',
                    borderTopColor: 'grey.150',
                }],
            )}
        >
            {(shouldReload || tableMetaData) && <Box sx={C1TableReduxStyles.reloadContainer}>
                {shouldReload && (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>
                            <FormattedMessage
                                id="component_c1_table_new_content"
                                defaultMessage="This list has been updated and needs to be refreshed."
                            />
                        </Typography>
                        <C1Button startIcon={<C1Icon icon={RefreshCw} />} onClick={onReload}>
                            <FormattedMessage id="component_c1_table_reload" defaultMessage="Refresh" />
                        </C1Button>
                    </Stack>
                )}
                {tableMetaData && <Box sx={C1TableReduxStyles.metadata}>{tableMetaData}</Box>}
            </Box>}
            <Box ref={tableScrollingContainerRef} sx={{ display: 'flex', flexDirection: 'column', overflow: 'auto', flexGrow: 1 }}>
                <Table
                    stickyHeader
                    size="small"
                    sx={sxc(C1TableReduxStyles.table, { gridTemplateColumns: grid.trim() })}
                >
                    <TableHead>
                        <TableRow>
                            {filteredCols.map((column, columnIndex) => {
                                const isSticky = column.isSticky && column.isSticky(staticExtraProps)
                                const isStickyAndAlignRight = isSticky && column.align !== 'left'
                                return (
                                    <TableCell
                                        key={column.field}
                                        scope="col"
                                        sx={sxc(
                                            [isSticky, C1TableReduxStyles.sticky],
                                        )}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isStickyAndAlignRight || column.align === 'right' ? 'flex-end' : 'flex-start', gap: 1, width: '100%' }}>
                                            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                                {column.onSort
                                                    ? <TableSortLabel
                                                        direction="desc"
                                                        onClick={(): void => handleSort(column)}
                                                        IconComponent={(): React.ReactElement => {
                                                            return sortDirection === 'desc' ? <C1Icon
                                                                icon={ChevronDown}
                                                                size="sm"
                                                                sx={sxc(C1TableReduxStyles.sortIcon, [
                                                                    sortByColumn === column.field,
                                                                    C1TableReduxStyles.sortIconActive,
                                                                ])}
                                                            /> : <C1Icon
                                                                icon={ChevronUp}
                                                                size="sm"
                                                                sx={sxc(C1TableReduxStyles.sortIcon, [
                                                                    sortByColumn === column.field,
                                                                    C1TableReduxStyles.sortIconActive,
                                                                ])}
                                                            />
                                                        }}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            variant={variant === 'minimal' ? 'caption' : undefined}
                                                            sx={sxc(
                                                                { color: 'text.primary' },
                                                                [
                                                                    column.alignmentOverrides === 'margin1',
                                                                    TableHeaderStyles.margin1,
                                                                ],
                                                                [
                                                                    column.alignmentOverrides === 'margin2',
                                                                    TableHeaderStyles.margin2,
                                                                ],
                                                            )}
                                                        >
                                                            {
                                                                column.label && typeof column.label === 'function'
                                                                    ? <column.label datas={data[page]} extras={staticExtraProps} />
                                                                    : column.label
                                                            }
                                                        </Typography>
                                                        {sortByColumn === column.field ? (
                                                            <Box component="span" sx={visuallyHidden}>
                                                                <FormattedMessage id="sorted" defaultMessage="sorted" />
                                                            </Box>
                                                        ) : null}
                                                    </TableSortLabel>
                                                    : <Typography
                                                        component="span"
                                                        variant={variant === 'minimal' ? 'caption' : undefined}
                                                        noWrap
                                                        sx={sxc(
                                                            { color: 'text.primary' },
                                                            [column.alignmentOverrides === 'margin1', TableHeaderStyles.margin1],
                                                            [column.alignmentOverrides === 'margin2', TableHeaderStyles.margin2],
                                                        )}
                                                    >
                                                        {
                                                            column.label && typeof column.label === 'function'
                                                                ? <column.label datas={data[page]} extras={staticExtraProps} />
                                                                : column.label
                                                        }
                                                    </Typography>
                                                }
                                            </Box>
                                            {(columnConfigurationIsActive && columnIndex === filteredCols.length - 1) &&
                                                configureColumnsButton}
                                        </Box>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody ref={tableBodyRef} onKeyDown={handleTableKeyDown} onFocus={handleTableFocus}>
                        <C1TableBody
                            getId={getId}
                            cols={filteredCols}
                            rows={data ? data[page] : []}
                            groupedRows={groupedData ? groupedData[page] : undefined}
                            isError={isError}
                            isLoading={isLoading}
                            emptyState={emptyState}
                            pageSize={pageSize}
                            staticExtraProps={staticExtraProps}
                            isRowError={isRowError}
                            ExpandedContent={ExpandedContent}
                            onRowClick={onRowClick}
                            selectionsAsMap={selectionsAsMap}
                            focusAnchorRefs={focusAnchorRefs}
                            initialFocusedRowIndex={focusedRowIndexRef.current}
                            getRowAriaLabel={getRowAriaLabel}
                        />
                    </TableBody>
                </Table>
            </Box>
            {columnConfigurationIsActive && (
                <Menu
                    id="column-customizer-menu"
                    anchorEl={columnCustomizerAnchorEl}
                    open={Boolean(columnCustomizerAnchorEl)}
                    onClose={(): void => setColumnCustomizerAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transitionDuration={100}
                    slotProps={{
                        paper: {
                            sx: {
                                width: 320,
                                maxHeight: 450,
                                overflow: 'auto',
                                borderRadius: C1BorderRadius.large,
                            },
                        },
                    }}
                >
                    <CustomColumnConfigurationUI
                        columns={canBeConfiguredCols}
                        selectedKeys={thisTableSavedColConfig || defaultColumnKeys}
                        onConfigChange={handleColumnConfigChange}
                        onReset={handleResetColumnConfig}
                        hasCustomConfig={!!thisTableSavedColConfig}
                    />
                </Menu>
            )}
        </TableContainer>
        {isError && <Box sx={{ borderRadius: 1, borderTopLeftRadius: 0, borderTopRightRadius: 0, border: 1, borderColor: 'error.light', borderTop: 0 }}>
            <APIError message={error?.message} code={error?.code} sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }} />
        </Box>}
        {onPageChange && !!data?.[0]?.length && variant === 'default' &&
            <Stack direction="row-reverse" alignItems="center" sx={C1TableReduxStyles.paginationWrapper}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {isBackgroundLoading && <C1CircularProgress size={16} aria-label={intl.formatMessage({ id: 'table_loading_more', defaultMessage: 'Loading more data' })} />}
                    <C1TablePagination
                        page={page}
                        pageSize={pageSize}
                        data={data}
                        canLoadMore={canLoadMore}
                        isLoading={isBackgroundLoading}
                        onPageChange={onPageChange}
                        totalCount={_totalCount}
                        sx={C1TableReduxStyles.tablePagination as SxProps}
                    />
                </Stack>

                {/* Aria-live region for screen reader announcements of selection changes */}
                <Box
                    component="span"
                    sx={visuallyHidden}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {selections?.length
                        ? intl.formatMessage(
                            { id: 'table_selections_live_region', defaultMessage: '{count} items selected' },
                            { count: selections.length },
                        )
                        : ''}
                </Box>

                {(selections && setSelections && !extraFooterActions) ? (
                    <DefaultSelectionsControl<T> selections={selections} setSelections={setSelections} />
                ) : extraFooterActions}
            </Stack>
        }
    </ErrorBoundary>
}

type DefaultSelectionsControlProps<T> = {
    selections: T[]
    setSelections: (selections: T[]) => void
}

const DefaultSelectionsControl = <T, >({ selections, setSelections }: DefaultSelectionsControlProps<T>): React.ReactElement => {
    return <Stack direction="row" spacing={1} alignItems="center">
        <Typography>
            <FormattedMessage
                id="component_c1_table_selected"
                defaultMessage="{count} items selected"
                values={{ count: selections.length }}
            />
        </Typography>
        {selections.length > 0 && <C1Button onClick={(): void => { setSelections([]) }}>
            <FormattedMessage id="component_c1_table_clear_selections" defaultMessage="Clear selections" />
        </C1Button>}
    </Stack>
}
